import {
	DASH_DEVICE_LABEL_CATEGORY_FALLBACK,
	resolvePresetId,
	type DashDeviceLabelConfig,
} from './presets';

export type DeviceConfigForDashLabels = {
	dashInfoLabels?: DashDeviceLabelConfig;
	family?: string;
	modes?: Record<string, { dashInfoLabels?: DashDeviceLabelConfig; category?: string } | undefined>;
};

export type DashLabelDeviceRef = {
	type?: string;
	mode?: string;
	category?: string;
} | null;

/**
 * Resolve the dash info-labels config for a device using the same precedence as
 * production: mode seed → device seed → category/family fallback.
 */
export const resolveDashInfoLabelsConfig = (
	devicesConfig: Record<string, DeviceConfigForDashLabels | undefined>,
	device: DashLabelDeviceRef,
): DashDeviceLabelConfig | undefined => {
	if (!device?.type) return undefined;

	const deviceConfig = devicesConfig[device.type];
	if (!deviceConfig) return undefined;

	const modeKey = device.mode;
	const modeConfig = modeKey ? deviceConfig.modes?.[modeKey] : undefined;

	if (modeConfig?.dashInfoLabels) return modeConfig.dashInfoLabels;
	if (deviceConfig.dashInfoLabels) return deviceConfig.dashInfoLabels;

	const category = modeConfig?.category || device.category || deviceConfig.family;
	if (category && DASH_DEVICE_LABEL_CATEGORY_FALLBACK[category]) {
		return DASH_DEVICE_LABEL_CATEGORY_FALLBACK[category];
	}

	return undefined;
};

export type CollectedDashInfoLabelSeed = {
	type: string;
	scope: 'device' | 'mode';
	mode?: string;
	presetId: string;
	raw: DashDeviceLabelConfig;
};

/** Walk `devices_config` and collect every explicit `dashInfoLabels` seed. */
export const collectDashInfoLabelSeeds = (
	devicesConfig: Record<string, DeviceConfigForDashLabels | undefined>,
): CollectedDashInfoLabelSeed[] => {
	const seeds: CollectedDashInfoLabelSeed[] = [];

	for (const [type, deviceConfig] of Object.entries(devicesConfig)) {
		if (!deviceConfig) continue;

		if (deviceConfig.dashInfoLabels != null) {
			const presetId = resolvePresetId(deviceConfig.dashInfoLabels);
			if (presetId) {
				seeds.push({
					type,
					scope: 'device',
					presetId,
					raw: deviceConfig.dashInfoLabels,
				});
			}
		}

		for (const [mode, modeConfig] of Object.entries(deviceConfig.modes ?? {})) {
			if (modeConfig?.dashInfoLabels == null) continue;
			const presetId = resolvePresetId(modeConfig.dashInfoLabels);
			if (!presetId) continue;
			seeds.push({
				type,
				scope: 'mode',
				mode,
				presetId,
				raw: modeConfig.dashInfoLabels,
			});
		}
	}

	return seeds;
};
